import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { OccupationalGroup } from '../../types/occupation';
import { ChevronDown } from './icons/ChevronDown';
import { cn } from '../../lib/utils';
import { ComboboxListbox, type ComboboxListItem } from './ComboboxListbox';

interface OccupationGroupComboboxProps {
  /** Currently selected occupational-group id. Empty string =
   *  "All occupational groups" (no narrowing). */
  readonly value: string;
  /** Fires with the picked group id, or '' when the user picks
   *  the "All occupational groups" default. */
  readonly onChange: (value: string) => void;
  readonly groups: ReadonlyArray<OccupationalGroup>;
  /** Visible label above the input. Also used as the
   *  accessible name on the combobox input. */
  readonly label?: string;
  /** Placeholder shown when the input is empty (no value, no query). */
  readonly placeholder?: string;
  /**
   * Optional per-OG provider counts (IMP-017). When provided, each
   * option label renders "(N)" next to the group name. Keyed by the
   * OG display NAME (matches `OccupationalGroup.name`), not the
   * chip id, so callers can pass the raw backend response through.
   *
   * Only the Providers callsite passes this prop today
   * (`ProvidersTable` -> `ProvidersFilterBar`); Detail / Map sidebar /
   * Rankings render badge-less because their semantics differ (Detail
   * is single-metro-already-scoped, Rankings is national). Defaults
   * to undefined (no badges) so unrelated callsites are unchanged.
   *
   * `null` is treated the same as undefined (no badges) -- callers
   * pass null to mean "counts still loading; render without badges
   * for now" without forcing a conditional render.
   *
   * An OG missing from the map renders without a badge (defensive
   * against backend drift, e.g. a 23rd OG ships before the frontend
   * catches up).
   */
  readonly counts?: Readonly<Record<string, number>> | null;
}

type ListItem = ComboboxListItem;

const DEFAULT_LABEL = 'All occupational groups';

/**
 * Single APG combobox (WAI-ARIA 1.2) for picking an occupational
 * group with typeahead. Built for the Detail filter bar where the
 * prior split "Search occupational groups..." input + dropdown
 * was confusing (the search filtered the chart/table but the
 * dropdown also filtered them; two controls for the same intent).
 *
 * IMP-003 (Mayuresh 2026-06-01 Zoom 10:30): "instead of having
 * sort of like a search bar that says search occupational groups,
 * but then you can also do a dropdown with the occupational
 * groups, I'm wondering if you can like combine that into one thing"
 *
 * Behavior:
 *   - Controlled by `value` (group id) + `onChange`. The visible
 *     input value shows the selected group's name when one is
 *     committed; while the listbox is open and the user is typing,
 *     it shows the query.
 *   - Opens on focus/click; closes on outside-click, Escape, or
 *     after a pick.
 *   - Filters the listbox by case-insensitive substring as the
 *     user types.
 *   - Always includes "All occupational groups" as the first
 *     option (value=''); picking it clears the selection.
 *   - Keyboard: ArrowDown / ArrowUp navigate the highlight,
 *     Enter commits the highlighted option, Escape closes.
 *
 * Modeled after `MetroSearchCombobox` (map sidebar). Kept as a
 * sibling component instead of generalizing for two reasons:
 *   1. The "default option" ("All ...") row is specific to filters
 *      that allow an empty value, which `MetroSearchCombobox`
 *      doesn't have (the map's metro picker has no "all metros").
 *   2. Premature abstraction risk -- a third caller would justify
 *      extracting a generic <Combobox> primitive.
 */
export function OccupationGroupCombobox({
  value,
  onChange,
  groups,
  label = 'Occupational Group',
  placeholder = 'Search occupational groups...',
  counts,
}: OccupationGroupComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [query, setQuery] = useState('');
  /** True while the user is actively typing -- swaps the displayed
   *  input value from "selected group name" to "raw query" so the
   *  listbox filter feels live. Reset on close + on commit. */
  const [isTyping, setIsTyping] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const reactId = useId();
  const listboxId = `ogcombobox-${reactId}-listbox`;
  const optionIdStem = `ogcombobox-${reactId}-opt`;

  // Stable sorted list (alphabetical) so the typeahead behaves
  // predictably regardless of `groups` prop ordering.
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.name.localeCompare(b.name)),
    [groups],
  );

  // Flat list including the leading "All" default. Built once per
  // groups or counts change; query filtering happens downstream in
  // `filtered`. The default "All occupational groups" option never
  // carries a badge -- it isn't an OG, so a count there would be
  // meaningless.
  //
  // Count rendering rules (IMP-017, 2026-06-05 badge update):
  //   - counts == null/undefined: no badges anywhere (Detail/Map/
  //     Rankings + the Providers loading state).
  //   - counts present + OG name in the map: render a right-aligned
  //     CountBadge via ComboboxListbox (replaces the old inline
  //     "(N)" suffix in the label string).
  //   - counts present + OG name MISSING from the map: no badge
  //     (defensive against backend drift -- a 23rd OG shipping
  //     before the frontend's chip<->name map catches up).
  const allItems = useMemo<ReadonlyArray<ListItem>>(() => {
    const items: Array<ListItem> = [
      { id: 'default', value: '', label: DEFAULT_LABEL },
    ];
    for (const g of sortedGroups) {
      const c = counts ? counts[g.name] : undefined;
      items.push({
        id: g.id,
        value: g.id,
        label: g.name,
        count: typeof c === 'number' ? c : undefined,
      });
    }
    return items;
  }, [sortedGroups, counts]);

  const filtered = useMemo<ReadonlyArray<ListItem>>(() => {
    if (!isTyping || !query.trim()) return allItems;
    const needle = query.trim().toLowerCase();
    return allItems.filter((it) => it.label.toLowerCase().includes(needle));
  }, [allItems, isTyping, query]);

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    const match = sortedGroups.find((g) => g.id === value);
    return match?.name ?? '';
  }, [sortedGroups, value]);

  // Outside click / Escape close.
  useEffect(() => {
    if (!isOpen) return;
    function onDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setIsTyping(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setIsTyping(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  // Reset highlight to top whenever the filtered list shape changes.
  useEffect(() => {
    setHighlight(0);
  }, [query, isOpen]);

  function commit(item: ListItem) {
    onChange(item.value);
    setIsOpen(false);
    setIsTyping(false);
    setQuery('');
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (isOpen && filtered[highlight]) {
        e.preventDefault();
        commit(filtered[highlight]!);
      }
    }
  }

  // What the input visually shows:
  //   - If the user is actively typing -> the query.
  //   - Otherwise -> the selected group's name (or '' if none).
  const displayValue = isTyping ? query : selectedLabel;

  const activeOptionId =
    isOpen && filtered[highlight]
      ? `${optionIdStem}-${filtered[highlight]!.id}`
      : undefined;

  return (
    <div ref={containerRef}>
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-4">
        {label}
      </span>
      <div className="relative">
        {/* Magnifier */}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-4"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsTyping(true);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          aria-label={label}
          autoComplete="off"
          // APG combobox pattern (WAI-ARIA 1.2)
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          className={cn(
            'block w-full rounded-lg border border-border bg-white py-2 pl-9 text-sm text-text-1 placeholder:text-text-4 transition hover:border-text-4 focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20',
            // Reserve room for both the clear (X) and the chevron
            // when a value is committed; otherwise just the chevron.
            value ? 'pr-14' : 'pr-9',
          )}
        />
        {/* Clear (X) button — appears only when a value is committed
            so reviewers can drop the filter with one click instead of
            hunting for the "All …" default row in the listbox
            (2026-07-02 Georgetown feedback: filters were hard to
            clear). Kept inside the input's flex row so it visually
            reads as part of the field. `mousedown` (not `click`) so
            the pick fires before the input's blur kills isOpen -- same
            pattern as ComboboxListbox. */}
        {value && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            title={`Clear ${label}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onChange('');
              setQuery('');
              setIsTyping(false);
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            // WCAG 2.2 SC 2.5.8 Target Size (Minimum): 24x24px hit target
            // (h-6 w-6; was h-5 w-5 = 20px). Mirrors MetroAreaCombobox.
            className="absolute right-8 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-text-4 transition hover:bg-ice hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            >
              <path d="M1 1L9 9M9 1L1 9" />
            </svg>
          </button>
        )}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-4 transition-transform',
            isOpen && 'rotate-180',
          )}
        />

        {isOpen && (
          <ComboboxListbox
            id={listboxId}
            ariaLabel="Occupational groups"
            items={filtered}
            highlight={highlight}
            selectedValue={value}
            optionIdStem={optionIdStem}
            onHover={setHighlight}
            onCommit={commit}
          />
        )}
      </div>
    </div>
  );
}
